import { NextApiRequest, NextApiResponse } from 'next';
import api from './api';
import { APIProxy } from './api-proxy';
import { IMenuCategory } from '../interface/content.interface';

const API_GATEWAY_URL_CONTENT = process.env.API_GATEWAY_URL_CONTENT;

const getFaqList = (
  { type, code, isLandingPage = true, page = 0, size = 10, config = {} },
  isUseGatewayAPI?: boolean
) => {
  return api.get(
    `content/v1/faq?type=${type}&code=${code}&isLandingPage=${isLandingPage}&page=${page}&size=${size}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT, ...config } : config,
    false,
    false,
    false
  );
};

const getServiceDetail = (
  serviceType,
  carModel,
  couponCode,
  vehicleCode,
  isUseGatewayAPI?: boolean
) => {
  if (vehicleCode) {
    return api.get(
      `content/v1/service/content?serviceType=${serviceType}&carModel=${carModel}&couponCode=${couponCode}&vehicleCode=${vehicleCode}`,
      isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
      false,
      false,
      false
    );
  } else {
    return api.get(
      `content/v1/service/content?serviceType=${serviceType}&carModel=${carModel}&couponCode=${couponCode}`,
      isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
      false,
      false,
      false
    );
  }
};

const getServiceBanner = ({ type, category }, isUseGatewayAPI?: boolean) => {
  return api.get(
    `content/v1/service?serviceType=${type}&serviceCategory=${category}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
    false,
    false,
    false
  );
};

const getTermCondition = ({ type, config = {} }, isUseGatewayAPI?: boolean) => {
  return api.get(
    `content/v1/termcondition?type=${type}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT, ...config } : config,
    false,
    false,
    false
  );
};

const getFAQServices = (serviceType, category, isUseGatewayAPI?: boolean) => {
  return api.get(
    `content/v1/faq?type=${serviceType}&code=${category}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
    false,
    false,
    false
  );
};

const getStaticPage = (pageType, isUseGatewayAPI?: boolean) => {
  return api.get(
    `content/v1/page/static-page?pageId=${pageType}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
    false,
    false,
    false
  );
};

const getServicePlan = ({ config = {} } = {}, isUseGatewayAPI?: boolean) => {
  return api.get(
    'content/v1/service/plan',
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT, ...config } : config,
    false,
    false,
    false
  );
};

const getServicePlanAscom = (id: string, isUseGatewayAPI?: boolean) => {
  return api.get(
    `content/v1/service/plan/${id}`,
    isUseGatewayAPI ? { baseURL: API_GATEWAY_URL_CONTENT } : {},
    false,
    false,
    false
  );
};

const getTrendingCar = (city: string, isUseGatewayAPI = false, isAutoPopulate = false) => {
  const params = {
    email: 'anonymous',
    city,
    ...(isAutoPopulate && { autoPopulate: true }),
  };

  const config = {
    ...(isUseGatewayAPI && { baseURL: API_GATEWAY_URL_CONTENT }),
    params,
  };

  return api.get('content/v1/page/trending-cars', config, false, false, false);
};

const getTrendingProduct = (city: string = '', userId?: string, isUseGatewayAPI?: boolean) => {
  return api.get(
    'content/v1/contentCatalog/trending-product-aftersales',
    isUseGatewayAPI
      ? { baseURL: API_GATEWAY_URL_CONTENT, params: { userId: userId || 'anonymous', city } }
      : { params: { userId: userId || 'anonymous', city } },
    false,
    false,
    false
  );
};

const getBackgroundAuth = () => {
  return api.get('content/v1/contentCatalog/login-page-banner', {}, false, false, false);
};

const getBannerLocation = () => {
  return api.get(
    'content/v1/page/static-page?componentId=Auto2000LocationPopupImageBannerComponent',
    {},
    false,
    false,
    false
  );
};

const getAccessoriesDetail = (code: string, config = {}) => {
  return api.get(`content/v1/accessories/detail/${code}`, config, false, false, false);
};

const getServiceHeaderAccessories = (pageType) => {
  return api.get(`content/v1/accessories/${pageType}`, {}, false, false, false);
};

const getFlashSale = (userId, category, city) => {
  return api.get(
    `content/v1/contentCatalog/flash-sale?userId=${userId}&category=${category}&city=${city}`,
    {},
    false,
    false,
    false
  );
};

const getNewestProduct = async (category, productCode) => {
  return await api.get(
    `api/get-newest-product?productCode=${productCode}&category=${category}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    },
    false,
    false,
    false
  );
};

const getMenuCategory = async (
  req: NextApiRequest,
  res: NextApiResponse
): Promise<IMenuCategory> => {
  const apiProxy = new APIProxy(req, res);
  const response = await apiProxy.makeRequest({
    url: 'saphybris-proxy/v1/saphybris/auto2000ContentCatalog/Online/auto2000content/menucategory',
    method: 'GET',
    params: {
      email: 'anonymous',
    },
  });
  return response.data;
};

const getStaticPageDetail = async (pageName: string) => {
  return api.get(`content/v1/page/${pageName}`, {}, false, false, false);
};

const getStaticPageComponentDetail = async (componentId: number) => {
  return api.get(`content/v1/page/components/${componentId}`, {}, false, false, false);
};

export default {
  getFaqList,
  getTrendingCar,
  getServiceDetail,
  getServiceBanner,
  getTermCondition,
  getFAQServices,
  getStaticPage,
  getServicePlan,
  getTrendingProduct,
  getBackgroundAuth,
  getAccessoriesDetail,
  getServiceHeaderAccessories,
  getFlashSale,
  getBannerLocation,
  getNewestProduct,
  getMenuCategory,
  getStaticPageDetail,
  getStaticPageComponentDetail,
  getServicePlanAscom,
};
